import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal } from "@/components/Reveal";
import { FAQS } from "@/components/FAQ";
import Seo from "@/components/Seo";

const TONES = {
  green: "bg-[#22C55E]/10 border-[#22C55E]/40 text-[#22C55E]",
  red: "bg-[#EF4444]/10 border-[#EF4444]/40 text-[#EF4444]",
  amber: "bg-[#F1B434]/10 border-[#F1B434]/40 text-[#F1B434]",
  cyan: "bg-[#2DACE2]/10 border-[#2DACE2]/40 text-[#2DACE2]",
  gold: "bg-[#FFD700]/10 border-[#FFD700]/40 text-[#FFD700]",
};

const Badge = ({ label, tone }) => (
  <span
    className={`shrink-0 inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] ${TONES[tone]}`}
  >
    {label}
  </span>
);

const RULE_SECTIONS = [
  {
    heading: "Your account",
    rules: [
      ["One account per trader, per brand",
       "You can hold one active account under MyFXPocket. Separate accounts under other prop firms are fine.",
       "1 Account", "cyan"],
      ["Letting someone else trade your account",
       "Your account is yours. You can't share logins, hand it to a money manager, or run a signal service that trades on your behalf.",
       "Not Allowed", "red"],
      ["Up to $400K total funded capital",
       "No limit on how many challenges you can buy. Once funded, the maximum capital allocated to you across all funded accounts is $400,000.",
       "$400K Cap", "gold"],
      ["30 days with no trades = auto-closed",
       "Leave your account with zero trades for 30 straight calendar days and it's closed automatically — the program ends and fees aren't refunded. Place a trade before the 30 days are up to keep it live.",
       "30-Day Limit", "amber"],
    ],
  },
  {
    heading: "Drawdown rules",
    rules: [
      ["Max daily loss is equity-based",
       "Two-Step: 5% · One-Step: 3% · Instant Funding: 3%. Calculated on your starting equity each day — hit it and the account is closed.",
       "Equity-Based", "cyan"],
      ["Max overall loss is a fixed floor (challenge accounts)",
       "For Two-Step (10%) and One-Step (6%), your max loss is calculated once from your initial balance and never changes. On a $100K Two-Step account, your floor is always $90K — regardless of profit.",
       "Fixed", "cyan"],
      ["Trailing max loss (Instant Funding & Instant Light)",
       "Your max loss floor (5%) rises with the highest equity your account reaches and never decreases. It is not recalculated when you take a payout — the floor stays exactly where your trading results moved it.",
       "Trailing", "cyan"],
    ],
  },
  {
    heading: "Payouts & consistency",
    rules: [
      ["You keep 80% of every profit",
       "Same split across all plans. Upgradable to 90/10 with the Profit Split Increase add-on (+20% extra fee on the challenge).",
       "80 / 20 Split", "gold"],
      ["How often you can request a payout",
       "Two-Step: every 5 days · One-Step: every 5 days · Instant Funding & Instant Light: every 14 days.",
       "Every 5–14 Days", "green"],
      ["25% Consistency Rule (Instant Light only)",
       "On Instant Funding Light accounts, no single trading day can account for more than 25% of your total profits when you request a payout. Example: best day $6,000 on $20,000 total = 30% — too high. Keep trading until your total reaches $24,000 so your best day is exactly 25%. Consistency % = (Highest Single Day Profit ÷ Total Profit) × 100. Standard Instant Funding has no consistency rule.",
       "Light Only", "amber"],
      ["KYC at payout only",
       "No KYC at registration or when you get funded. Identity verification happens when you request your first payout — passport or national ID card accepted globally.",
       "Payout Only", "green"],
      ["Maximum 5 payouts per funded account",
       "Funded accounts, including Instant Funding, are limited to 5 payouts. After the fifth payout the account is permanently closed and any remaining simulated capital or unpaid rewards are forfeited. To continue trading, purchase a new challenge — no refund of fees is due on closure.",
       "5 Payouts Max", "amber"],
    ],
  },
  {
    heading: "Trading behavior",
    rules: [
      ["Overnight holding",
       "You can hold positions overnight across sessions.",
       "Allowed", "green"],
      ["Weekend holding",
       "Not allowed on standard plans — all positions must be closed before the weekend. Exception: Swing plans allow both overnight and weekend holding (triple Friday swap applies).",
       "Not Allowed", "red"],
      ["Hedging across multiple accounts",
       "Prohibited. Opening opposite positions on related accounts — yours or anyone else's — including coordinated trading with other traders.",
       "Prohibited", "red"],
      ["Wildly different sizing between challenge and funded",
       "Trade your funded account the same way you traded the challenge. Suddenly going from 0.1 lots to 10 lots is a red flag.",
       "Red Flag", "amber"],
      ["Earnings-gap or close-to-open exploits",
       "Holding positions specifically into an earnings release (or similar) to grab the gap is treated as exploiting the simulated environment.",
       "Prohibited", "red"],
    ],
  },
  {
    heading: "News & events",
    rules: [
      ["News trading during the challenge",
       "On challenge and evaluation accounts, trade news freely — the news-window restriction applies to Funded Accounts only.",
       "Allowed", "green"],
      ["News window — Funded Accounts only",
       "On a Funded Account, no opening or executing trades within 5 minutes before or 5 minutes after major economic news (NFP, CPI, central bank announcements, etc.).",
       "Restricted ±5 Min", "amber"],
      ["Holding existing positions through news",
       "Positions opened before the 5-minute window and closed after it are fine. The rule only catches new orders placed inside that 10-minute window.",
       "Allowed", "green"],
    ],
  },
  {
    heading: "Tech & tools",
    rules: [
      ["Your trading platform is Match-Trader",
       "All MyFXPocket accounts run on Match-Trader, provided by Match-Trade Technologies LLC — institutional-grade execution, deep liquidity and reliable uptime.",
       "Platform", "gold"],
      ["Off-the-shelf 'challenge pass' bots / EAs",
       "Any bot, script, EA or third-party algorithm marketed as designed to pass prop firm challenges is prohibited. Same goes for high-frequency or mass-order strategies aimed at gaming the system.",
       "Prohibited", "red"],
      ["Exploiting price errors or latency",
       "If you spot a wrong quote, a stale feed or a system glitch — knowingly trading off it is a violation.",
       "Prohibited", "red"],
      ["VPN / proxy to bypass region restrictions",
       "If your country is on the restricted list, you can't use a VPN to get in. This is treated as fraud and forfeits any rewards.",
       "Prohibited", "red"],
      ["Your own indicators & analysis tools",
       "TradingView, custom Pine Script, your own spreadsheets — all fine. The restriction is on automation that places orders for you with intent to game the system, not on your decision-making tools.",
       "Allowed", "green"],
    ],
  },
  {
    heading: "If you break the rules",
    rules: [
      ["The violating trades may be excluded",
       "If we determine a trade was a violation, that trade can be excluded from your results — it doesn't count toward your profit or against your drawdown.",
       "Trade Excluded", "amber"],
      ["Reward for the period can be denied",
       "For a serious or repeated violation in a payout period, the reward for that period can be cancelled entirely.",
       "Reward Denied", "red"],
      ["Account termination, no refund",
       "For material breaches (bots designed to game the system, multi-account hedging, KYC fraud, etc.), the account is terminated. Challenge fees are not refunded and accrued unpaid rewards are forfeited.",
       "Termination", "red"],
      ["Violations can affect all your accounts",
       "Coordinated activity across multiple accounts — yours or with other traders — can result in action against all of them.",
       "All Accounts", "red"],
    ],
  },
];

export default function Rules() {
  return (
    <main className="pt-32 pb-24 lg:pb-32" data-testid="rules-page">
      <Seo
        title="Trading Rules — MyFXPocket"
        description="Every MyFXPocket rule in plain English: drawdown limits, news trading, payouts, the 25% consistency rule and what happens if you break the rules."
        path="/rules"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex items-center gap-4 mb-6">
            <span className="eyebrow">Rules</span>
            <span className="h-px w-10 bg-gradient-to-r from-[#FFD700] to-transparent" />
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#999999]">Last updated · October 2026</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-[-0.05em] text-white leading-[1.1]">
            The rules, <span className="gold-text">in plain English.</span>
          </h1>
          <p className="mt-5 text-base text-[#999999] leading-relaxed max-w-2xl">
            Every rule that applies to MyFXPocket Two-Step, One-Step, Instant Funding and Instant
            Funding Light accounts on the Match-Trader platform. No fine print, no surprises.
          </p>
        </Reveal>

        <Reveal className="mt-8">
          <div
            data-testid="rules-badge-legend"
            className="glass-card rounded-xl px-5 py-4 flex flex-wrap items-center gap-x-6 gap-y-3"
          >
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#999999]">Badge key:</span>
            {[
              ["Allowed", "green", "Permitted — trade freely"],
              ["Restricted", "amber", "Conditional — check the rule"],
              ["Not Allowed", "red", "Prohibited — violation risk"],
              ["Info", "cyan", "How the parameter works"],
              ["Highlight", "gold", "Key value or platform detail"],
            ].map(([label, tone, hint]) => (
              <span key={label} className="inline-flex items-center gap-2">
                <Badge label={label} tone={tone} />
                <span className="text-xs text-[#999999]">{hint}</span>
              </span>
            ))}
          </div>
        </Reveal>

        <div className="mt-14 space-y-12">
          {RULE_SECTIONS.map((sec, i) => (
            <Reveal key={sec.heading} delay={0.05}>
              <section data-testid={`rules-page-section-${i}`}>
                <h2 className="text-xl sm:text-2xl font-semibold tracking-[-0.03em] text-white mb-5 flex items-baseline gap-3">
                  <span className="text-xs font-bold text-[#FFD700]">{String(i + 1).padStart(2, "0")}</span>
                  {sec.heading}
                </h2>
                <div className="space-y-3">
                  {sec.rules.map(([title, body, badge, tone], j) => (
                    <div key={j} className="glass-card rounded-xl p-5" data-testid={`rules-page-rule-${i}-${j}`}>
                      <div className="flex items-start justify-between gap-4">
                        <h3 className="text-sm font-semibold text-white">{title}</h3>
                        <Badge label={badge} tone={tone} />
                      </div>
                      <p className="text-sm text-[#999999] leading-relaxed mt-1.5">{body}</p>
                    </div>
                  ))}
                </div>
              </section>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-20">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-[-0.04em] text-white mb-8">
            Frequently asked <span className="gold-text">questions.</span>
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map(([q, a], i) => (
              <AccordionItem
                key={i}
                value={`rf-${i}`}
                data-testid={`rules-page-faq-${i}`}
                className="glass-card rounded-xl border-white/5 px-5 sm:px-6 overflow-hidden"
              >
                <AccordionTrigger className="text-left text-sm sm:text-base font-semibold text-white hover:text-[#FFD700] hover:no-underline py-5 [&[data-state=open]]:text-[#FFD700]">
                  {q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-[#999999] leading-relaxed pb-5">
                  {a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </main>
  );
}
